import { useEffect, useState } from "react";
import { Badge, Button, Modal } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("bg-BG", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

function getActionBadge(action) {
  if (action === "create") {
    return <Badge bg="success">Create</Badge>;
  }

  if (action === "update") {
    return <Badge bg="primary">Update</Badge>;
  }

  if (action === "delete") {
    return <Badge bg="danger">Delete</Badge>;
  }

  return <Badge bg="secondary">{action}</Badge>;
}

function renderChanges(changes) {
  const entries = Object.entries(changes ?? {});

  if (!entries.length) {
    return <span className="text-muted">-</span>;
  }

  return (
    <div className="d-flex flex-column gap-1">
      {entries.map(([field, change]) => {
        const isChangePair = (
          change !== null &&
          typeof change === "object" &&
          !Array.isArray(change) &&
          (Object.prototype.hasOwnProperty.call(change, "old") || Object.prototype.hasOwnProperty.call(change, "new"))
        );

        return (
          <div key={field}>
            <span className="fw-semibold">{field}</span>: {isChangePair ? (
              <>
                {formatValue(change.old)} {"->"} {formatValue(change.new)}
              </>
            ) : (
              formatValue(change)
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function UserAuditLogsModal({ show, onClose, user }) {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show || !user?.id) {
      setAuditLogs([]);
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadAuditLogs = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetchWithAuth(`/api/users/${user.id}/audit-logs`);

        if (!res.ok) {
          const responseError = await res.json().catch(() => null);
          throw new Error(responseError?.error || "Failed to load audit logs");
        }

        const data = await res.json();

        if (!cancelled) {
          setAuditLogs(data.auditLogs ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setAuditLogs([]);
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAuditLogs();

    return () => {
      cancelled = true;
    };
  }, [show, user]);

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Audit logs - {user?.name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div className="alert alert-danger py-2">{error}</div>
        )}

        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <div className="d-flex justify-content-end mb-2">
              <span className="badge text-bg-secondary">Total: {auditLogs.length}</span>
            </div>

            <div className="table-responsive" style={{ maxHeight: "65vh", overflowY: "auto" }}>
              <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: "0.92rem" }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ position: "sticky", top: 0, width: "170px" }}>When</th>
                    <th style={{ position: "sticky", top: 0, width: "120px" }}>Entity</th>
                    <th style={{ position: "sticky", top: 0, width: "90px" }}>ID</th>
                    <th style={{ position: "sticky", top: 0, width: "90px" }}>Action</th>
                    <th style={{ position: "sticky", top: 0 }}>Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        No audit logs found.
                      </td>
                    </tr>
                  ) : auditLogs.map((entry, index) => (
                    <tr
                      key={entry.id}
                      style={{ "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff" }}
                    >
                      <td>{formatDateTime(entry.createdAt)}</td>
                      <td>{entry.entityType}</td>
                      <td>{entry.entityId}</td>
                      <td>{getActionBadge(entry.action)}</td>
                      <td>{renderChanges(entry.changes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
